import { ESectionLayout } from "../enums/SectionLayouts";
import { TImageAsset } from "./Asset";
import { ILocale } from "./Locale";
import { IUserData } from "./Users";
import { IColorScheme } from "@/utilities/types/ColorScheme.ts";

export enum EShowOnlyVerifiedLeadStatus {
  ON = "on",
  OFF = "off",
}

export type TAgencyDomain = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  name: string;
  privateKey: string;
  primary: boolean;
  publicKey: string;
  configId: string;
};

export type TAgencySocial = {
  url: string;
  type: string;
  label: string;
};

export type TAgencyMessage = {
  backgroundColor: string;
  text: string;
};

export type TDomain = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  name: string;
  privateKey: string;
  primary: boolean;
  publicKey: string;
  configId: string;
};

export type TAgencySetting = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  userId: string;
  siteTitle: {
    en: string;
  };
  siteDescription: {
    en: string;
  };
  siteLogoId: string;
  headerLogoId: string;
  footerLogoId: string;
  coverImageId: string;
  agencyPhotoId: string;

  headerLogo: TImageAsset;
  siteLogo: TImageAsset;
  footerLogo: TImageAsset;
  coverImage: TImageAsset;
  agencyPhoto: TImageAsset;
  phoneNumber: string;
  topMessage: TAgencyMessage;
  socialLinks: TAgencySocial[];
  domains: TDomain[];
  colorScheme: IColorScheme[];
  agencyName: string;
  agencyEmail: string;
  user: IUserData;
  promotionHeading: string;
  promotionBgColor: string;
  promotionSeoImageId: string;
  promotionSeoDescription: string;
  promotionSeoTitle: string;
  upcomingSeoImageId: string;
  upcomingSeoDescription: string;
  upcomingSeoTitle: string;
  topSeoImageId: string;
  topSeoDescription: string;
  topSeoTitle: string;
  seoImageId: string;
  seoDescription: string;
  promotionSeoImage: TImageAsset;
  upcomingSeoImage: TImageAsset;
  topSeoImage: TImageAsset;
  seoImage: TImageAsset;
  twitterCreator: string;
  twitterSite: string;
  mobileMastheadImageId: string;
  mobileMastheadImage: TImageAsset;
  floatingButtonEnabled: boolean;
  whatsapp: string;
  salesTeamInfo: {
    name?: string;
    registrationNumber?: string;
    currentEa?: string;
    licenseNumber?: string;
  };
  headerScript?: string;
  bodyScript?: string;
  enquiryScript?: string;
  isHiddenWhatsapp?: boolean;
  isHiddenShowFlatNumber?: boolean;
  isHiddenRegister?: boolean;
  isHiddenPricing?: boolean;
  privyrEnabled?: boolean;
  privyrUrl?: string;
  ignoreUnverifiedLeads?: boolean;
  enable2ndEnquiryForm?: boolean;
  enableProgressiveFormSimplified?: boolean;
  progressiveFormSimplifiedText?: string;
  // leadSMSNotification?: boolean;
  phoneNumberCallButton?: string;
  showOnlyVerifiedLeadStatus?: EShowOnlyVerifiedLeadStatus;
};

export type TGoogleAdsAccount = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  name: string;
  accountId: string;
  conversionId: string;
  loginCustomerId?: string | null;
  submitLeadFormConversionActionId?: string | null;
  qualifiedLeadConversionActionId?: string | null;
  notes?: string | null;
};

export type TGoogleAdsAccountCreate = {
  name: string;
  accountId: string;
  conversionId?: string;
  loginCustomerId?: string;
  submitLeadFormConversionActionId: string;
  qualifiedLeadConversionActionId: string;
  notes?: string;
};

export type TGoogleAdsAccountUpdate = Partial<TGoogleAdsAccountCreate>;

export type TAgencySettingUpdate = {
  siteTitle?: string;
  siteDescription?: string;
  phoneNumber?: string;
  phoneNumberCallButton?: string;
  siteLogoId?: string | null;
  headerLogoId?: string | null;
  footerLogoId?: string | null;
  agencyLogoId?: string | null;
  coverImageId?: string | null;
  agencyPhotoId?: string | null;
  socialLinks?: TAgencySocial[];
  topMessage?: TAgencyMessage;
  colorScheme?: IColorScheme[];
  agencyName?: string;
  agencyEmail?: string;
  promotionHeading?: string;
  promotionBgColor?: string;
  promotionSeoImageId: string | null;
  promotionSeoDescription: string;
  promotionSeoTitle: string;
  upcomingSeoImageId: string | null;
  upcomingSeoDescription: string;
  upcomingSeoTitle: string;
  topSeoImageId: string | null;
  topSeoDescription: string;
  topSeoTitle: string;
  seoImageId: string | null;
  seoDescription: string;
  twitterCreator: string;
  twitterSite: string;
  mobileMastheadImageId: string | null;
  floatingButtonEnabled: boolean;
  whatsapp?: string;
  salesTeamInfo: {
    name?: string;
    registrationNumber?: string;
    currentEa?: string;
    licenseNumber?: string;
  };
  headerScript?: string;
  bodyScript?: string;
  enquiryScript?: string;
  isHiddenWhatsapp?: boolean;
  isHiddenShowFlatNumber?: boolean;
  isHiddenRegister?: boolean;
  isHiddenPricing?: boolean;
  privyrUrl?: string;
  privyrEnabled?: boolean;
  ignoreUnverifiedLeads?: boolean;
  enable2ndEnquiryForm?: boolean;
  showOnlyVerifiedLeadStatus?: EShowOnlyVerifiedLeadStatus;
};

export type TAgencySettingCreate = {
  siteTitle: string;
  siteDescription: string;
  domain: string;
  salesTeamInfo?: {
    name?: string;
    registrationNumber?: string;
    currentEa?: string;
    licenseNumber?: string;
  };
  phoneNumber?: string;
};

export interface ISectionConfig {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  configId: string;
  layout: ESectionLayout;
  background: string;
  visible: boolean;
  name: ILocale | string;
  html: null | string;
  weight: number; // This field is used  to order
  weightTimestamp: string;
  photo: TImageAsset;
  photoId: string;
  cta: {
    title: string;
    button: {
      url: string;
      title: string;
      backgroundColor: string;
    };
    description: string;
  };
  location: null | {
    id: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    name: {
      en: string;
    };
    slug: string;
  };
}

export interface IDomainDTO {
  configId: string;
  primary: boolean;
  name: string;
}

export interface ICNameTargetDomains {
  cNameTargetDomain: string;
  cNameTarget: string;
}
